import {useContext} from 'react';
import {useRouterHistory as useHistory} from 'coreUtils/adapters/router';
import {ServerContext} from 'coreUtils/adapters/sdk';
import {isShellFlushed, reportLateHeader} from 'coreUtils/ssrShell';
import {isServer} from 'coreUtils/utils';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

/**
 * Returns a function that triggers an SSR redirect via flag.
 * On the server, it sets `res.locals._ssrRedirect` which is picked up
 * by the renderer (`react-rendering.js`, `react-router/entry.server`) before the response starts:
 * after the shell flush (Vite brands) it is lost and only logged.
 * On the client, it performs a history.push with the locale-prefixed destination.
 *
 * @param maxCacheAge - When provided, `res.setCache(maxCacheAge)` is called
 *   before setting the redirect flag. Omit if the caller sets cache separately
 *   (e.g. via `usePageInit` or `res.setCache` directly).
 */
const useSSRRedirect = () => {
	const ctx = useContext(ServerContext) as {
		res?: {setCache?: (seconds: number) => void; locals?: Record<string, unknown>; headersSent?: boolean};
	} | null;
	const res = ctx?.res ?? null;
	const {managerRepository} = useGeneralProvider();
	const history = useHistory();

	return (statusCode: number, destination: string, maxCacheAge?: number) => {
		if (!isServer) {
			const localeDestination = managerRepository.getLocalesMgr().updateLinkWithLocale(destination);

			history.push(localeDestination);

			return;
		}

		if (res && isShellFlushed(res)) {
			reportLateHeader(res, `redirect ${statusCode} ${destination}`);

			return;
		}

		if (res) {
			if (maxCacheAge !== undefined && res.setCache) {
				res.setCache(maxCacheAge);
			}

			res.locals._ssrRedirect = {statusCode, destination};
			(res.locals._abortSSRRender as (() => void) | undefined)?.();
		}
	};
};

export default useSSRRedirect;
